import styled from '@emotion/styled'
import { MotionStyle } from 'framer-motion'
import React, { Component, ReactNode } from 'react'
import Hotkeys from 'react-hot-keys'
import { api, ResourcePage, RfidAntennas, TmrRfidAntenna, UserManuals } from 'stylewhere/api'
import {
  Box,
  Button,
  EmulationButton,
  EmulationModal,
  Header,
  HeaderAction,
  HeaderDetail,
  Icons,
  Modal,
  ModalTitle,
  PageFormDetail,
  SliderSettings,
  Spacer,
  Text,
  ToggleField
} from 'stylewhere/components'
import {
  AppStore,
  isNewEmulationFormat,
  OperationConfig,
  parseNewEmulationFormat,
  RfidReader,
  Router,
  setArgsParams
} from 'stylewhere/shared'
import { __, T } from 'stylewhere/shared/i18n'
import {
  AutocompleteOperationUserSetting,
  OperationUserSetting,
  SelectOperationUserSetting,
  UserSettingSelectOption
} from 'stylewhere/types'
import { __CAPITALIZE, showToastError } from 'stylewhere/utils'

type UserSettingValue = string | number | boolean

export type PageHeader = { details?: HeaderDetail; actions?: HeaderAction[] }

interface Props {
  title: string
  onBackPress?: (() => void) | false
  alertOnBackPress?: boolean
  header?: PageHeader | null
  loading?: boolean
  headerRight?: React.ReactElement
  children?: any
  enableEmulation?: boolean
  forceDetails?: 'top' | 'right' | false
  contentWrapperColumn?: boolean
  activeAntennaSettings?: boolean
  styleSettingsButton?: React.CSSProperties
  userSettings?: { operation: OperationConfig; settings: OperationUserSetting[] }
  onUserSettingChanged?: (settingName: string, newValue: UserSettingValue) => void
}

interface State {
  showEmulationModal: boolean
  isEmulationMenuOpen: boolean
  antennasWorkstation: TmrRfidAntenna[]
  showUserSettingsModal: boolean
  userSettings: { [settingName: string]: UserSettingValue }
  userSettingsAutocompleteOptions: { [fieldCode: string]: UserSettingSelectOption[] }
}

export class Page extends Component<Props, State> {
  static Title = styled(Box)`
    font-weight: bold;
    font-size: 30px;
  `

  static TopBar = ({ children }: { children: React.ReactNode }) => {
    return (
      <Box mb={40} pv={10} row>
        {children}
      </Box>
    )
  }

  static Sidebar = ({
    style,
    width = 360,
    children,
    pl,
    pr,
    pb,
    pt,
    height,
    hideBar
  }: {
    style?: MotionStyle
    width?: string | number
    children: React.ReactNode
    pl?: number
    pr?: number
    pb?: number
    pt?: number
    height?: string | number
    hideBar?: boolean
  }) => {
    return (
      <Box
        hideBar={hideBar}
        style={{ overflowY: 'auto', padding: '4px 4px', boxSizing: 'border-box', ...style }}
        width={width}
        bgGrey
      >
        <Box flex pt={pt ?? 15} pb={pb ?? 15} pl={pl ?? 15} pr={pr ?? 4} height={height}>
          {children}
        </Box>
      </Box>
    )
  }

  static Content = ({
    children,
    style,
    notBoxed = false,
    overflowY
  }: {
    style?: MotionStyle
    children: React.ReactNode
    notBoxed?: boolean
    overflowY?: 'visible' | 'hidden' | 'clip' | 'scroll' | 'auto'
  }) => {
    return (
      <ContentInner overflowY={overflowY} flex style={style} notBoxed={notBoxed}>
        {children}
      </ContentInner>
    )
  }

  state: State = {
    showEmulationModal: false,
    isEmulationMenuOpen: false,
    antennasWorkstation: [],
    showUserSettingsModal: false,
    userSettings: {},
    userSettingsAutocompleteOptions: {}
  }

  getDataFromHost = () => {
    let host = globalThis.location.host
    let data: string[] = []
    if (!host.includes('localhost')) {
      host = host.replace('https://', '')
      host = host.replace('station.', '')
      const splitData = host.split('.')
      host = splitData[0]
      host = host.replace('-stw', '')
      data = host.split('-')
    }
    return {
      client: data.length > 0 && data[0] !== 'stylewhere' ? __CAPITALIZE(data[0]) : undefined,
      envinroment: data.length > 1 ? data[1].toUpperCase() : undefined
    }
  }

  getOperationOptionValue = (setting: OperationUserSetting, operationOptionValue: any) => {
    if (typeof operationOptionValue === 'string' && setting.type === 'number') {
      return Number(operationOptionValue)
    }
    if (typeof operationOptionValue === 'string' && setting.type === 'boolean') {
      return operationOptionValue === 'true'
    }
    return operationOptionValue
  }

  getUserSettingDefault = (setting: OperationUserSetting) => {
    const { userSettings } = this.props
    let defaultValue: UserSettingValue | undefined = setting.default
    if (defaultValue === undefined) {
      const operationOptionValue = userSettings.operation.options[setting.name] ?? userSettings.operation[setting.name]
      if (operationOptionValue === undefined) {
        defaultValue = setting.type === 'boolean' ? false : setting.type === 'number' ? 0 : ''
      } else {
        defaultValue = this.getOperationOptionValue(setting, operationOptionValue)
      }
    }
    return defaultValue
  }

  getUserSettings = async () => {
    const { userSettings } = this.props
    const tmpUserSettings: { [settingName: string]: UserSettingValue } = {}
    if (userSettings) {
      for (const setting of userSettings.settings) {
        const storedUserSetting: { value: UserSettingValue; isDefault: boolean } | undefined =
          await AppStore.getUserSetting(setting.name, userSettings.operation.id)
        const defaultValue: UserSettingValue | undefined = this.getUserSettingDefault(setting)
        const defaultValueToSet =
          setting.type === 'select'
            ? ((setting as SelectOperationUserSetting).options?.find((o) => o.key === defaultValue)?.value.toString() ??
              defaultValue)
            : defaultValue
        if (!storedUserSetting || (storedUserSetting.isDefault && storedUserSetting.value !== defaultValueToSet)) {
          tmpUserSettings[setting.name] = defaultValueToSet
          this.handleUserSettingsChange(setting.name, defaultValueToSet, true)
        } else {
          tmpUserSettings[setting.name] = storedUserSetting.value
          this.handleUserSettingsChange(setting.name, storedUserSetting.value, storedUserSetting.isDefault)
        }
      }
    }
    return tmpUserSettings
  }

  handleUserSettingsInitialization = async () => {
    const { activeAntennaSettings } = this.props

    let antennas = []
    const tmpUserSettings: { [settingName: string]: UserSettingValue } = await this.getUserSettings()

    if (activeAntennaSettings && AppStore.defaultWorkstation) {
      antennas = AppStore.defaultWorkstation.antennas ?? []
      if (antennas.length === 0) {
        const tmpAntennas = await Promise.all(
          AppStore.defaultWorkstation.rfidAntennaIds.map(async (antennaId) => {
            return RfidAntennas.get<TmrRfidAntenna>(antennaId).catch(function (err) {
              return undefined
            })
          })
        )
        tmpAntennas.forEach((antenna) => {
          if (antenna?.id) antennas.push(antenna)
        })
      }
    }
    this.setState({ userSettings: tmpUserSettings, antennasWorkstation: antennas })
  }

  fetchAutocompleteOptions = async () => {
    try {
      const autocompleteSettings = this.props.userSettings?.settings.filter(
        (setting) => setting.type === 'autocomplete'
      ) as AutocompleteOperationUserSetting[]
      if (!autocompleteSettings || autocompleteSettings.length === 0) return
      const options: { fieldCode: string; options: UserSettingSelectOption[] }[] = await Promise.all(
        autocompleteSettings.map(async (setting) => {
          if (!setting.endpoint?.url) return { fieldCode: setting.name, options: [] }
          const finalEndpoint = setArgsParams(setting.endpoint.url)
          let data =
            setting.endpoint.method === 'POST'
              ? (await api.post<ResourcePage<any>>(finalEndpoint)).data.content
              : (await api.get<ResourcePage<any>>(finalEndpoint)).data.content
          data = data.map(
            (item) =>
              ({
                key: item.id,
                label: item.description ?? item.code,
                value: item.code
              }) as UserSettingSelectOption
          )
          return { fieldCode: setting.name, options: data }
        })
      )
      this.setState({
        userSettingsAutocompleteOptions: options.reduce(
          (acc, { fieldCode, options: opts }) => {
            acc[fieldCode] = opts
            return acc
          },
          {} as { [fieldCode: string]: UserSettingSelectOption[] }
        )
      })
    } catch (error: any) {
      console.error('Error fetching autocomplete options:', error)
      showToastError(error.message, 'Error fetching autocomplete options')
    }
  }

  componentDidMount() {
    this._init()
  }

  _init = async () => {
    await this.handleUserSettingsInitialization()
    const data = this.getDataFromHost()
    let title = 'Stylewhere'
    if (data.client) title += ' ' + data.client
    title += ` | RFID Station - ${this.props.title}`
    if (title !== document.title) {
      if (data.envinroment) title = '[' + data.envinroment + '] ' + title
      document.title = title
    }

    if (this.props.alertOnBackPress) {
      // Prevents user from leaving the page and shows a confirmation message.
      // Unfortunately modern browsers removed support for custom alert messages.
      window.addEventListener('beforeunload', this.preventDefaultBeforeUnload)
    }
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.preventDefaultBeforeUnload)
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevProps.alertOnBackPress !== this.props.alertOnBackPress) {
      if (this.props.alertOnBackPress) {
        window.addEventListener('beforeunload', this.preventDefaultBeforeUnload)
      } else {
        window.removeEventListener('beforeunload', this.preventDefaultBeforeUnload)
      }
    }
    if (!prevState.showUserSettingsModal && this.state.showUserSettingsModal) {
      this.fetchAutocompleteOptions()
    }
  }

  preventDefaultBeforeUnload(e: BeforeUnloadEvent | PopStateEvent) {
    e.preventDefault()
  }

  onEmulationConfirm = (input?: string, options?: { skipStopCallbacks?: boolean }) => {
    this.setState({ showEmulationModal: false })
    if (!input) return
    if (isNewEmulationFormat(input)) {
      const emulatedTags = parseNewEmulationFormat(input)
      RfidReader.emulateTagAuto(emulatedTags, options?.skipStopCallbacks ?? false)
    } else {
      const codes: string[] = String(input).replaceAll(' ', '').split(',') ?? []
      RfidReader.emulateTagAuto(
        codes.map((code) => (AppStore.emulationIdentifierType === 'UHF' ? { epc: code } : { uid: code })),
        options?.skipStopCallbacks ?? false
      )
    }
  }

  onKeyDown = () => {
    this.setState((prevState) => ({
      showEmulationModal: !prevState.showEmulationModal
    }))
  }

  getFormSchemaLength = () => {
    const { header } = this.props
    if (header?.details?.formSchema) return header?.details.formSchema.filter((field) => field.type !== 'hidden').length
    return 0
  }

  getAvailableUserSettings = () => {
    const { userSettings } = this.props
    const result: OperationUserSetting[] = []
    if (!userSettings) return result
    userSettings.settings.forEach((setting) => {
      if (setting.visible) result.push(setting)
    })
    return result
  }

  availableAntennaSettings = () => {
    const { antennasWorkstation } = this.state
    const { activeAntennaSettings } = this.props
    const isAdmin = AppStore.isAdmin()
    // const isAdminMode = await AppStore.getAdminMode()
    const hasAntennaModalCapabilities = AppStore.hasAnyCapabilities('Configuration.AntennaModal.Edit')
    return (
      activeAntennaSettings &&
      (hasAntennaModalCapabilities || isAdmin) &&
      antennasWorkstation.length > 0 &&
      !RfidReader.isReading()
    )
  }

  enableUserSettings = () => {
    const { userSettings } = this.props
    const { antennasWorkstation } = this.state
    const availableUserSettings = this.getAvailableUserSettings()
    return {
      enabled:
        this.availableAntennaSettings() ||
        availableUserSettings.length > 0 ||
        userSettings?.operation.userManualFilename,
      userSettings: availableUserSettings,
      antennas: antennasWorkstation,
      userManualFilename: userSettings?.operation.userManualFilename,
      operationId: userSettings?.operation.id
    }
  }

  renderUserSettingsButton = () => {
    const data = this.enableUserSettings()
    if (!data.enabled) return null
    return (
      <SettingsButton
        data-testid={`user-settings-button`}
        style={this.props.styleSettingsButton ?? { marginLeft: 15 }}
        onClick={() => this.setState({ showUserSettingsModal: true })}
      >
        <Icons.Settings style={{ width: 36, margin: 17, opacity: 0.8 }} />
      </SettingsButton>
    )
  }

  handleUserSettingsChange = async (settingName: string, newValue: UserSettingValue, isDefault: boolean = false) => {
    if (!this.props.userSettings) return
    await AppStore.setUserSetting(settingName, newValue, this.props.userSettings.operation.id, isDefault)
    this.setState((prevState) => ({
      userSettings: { ...prevState.userSettings, [settingName]: newValue }
    }))

    this.props.onUserSettingChanged?.(settingName, newValue)
  }

  isMediol40 = (antenna: TmrRfidAntenna) => {
    return antenna.reader.deviceType === 'mediol-40'
  }

  isMediop200u = (antenna: TmrRfidAntenna) => {
    return antenna.reader.deviceType === 'mediop-200u'
  }

  isNordicSampo = (antenna: TmrRfidAntenna) => {
    return antenna.reader.deviceType === 'nordic-id-sampo-native'
  }

  isZebraFxSeries = (antenna: TmrRfidAntenna) => {
    return antenna.reader.deviceType === 'zebra-fx-series'
  }

  getPowerRange = (antenna: TmrRfidAntenna) => {
    if (this.isNordicSampo(antenna)) {
      return {
        min: 10,
        max: 27,
        step: 1
      }
    }
    if (this.isZebraFxSeries(antenna)) {
      return {
        min: 10,
        max: 29,
        step: 1
      }
    }
    if (this.isMediol40(antenna)) {
      return {
        min: 500,
        max: 5000,
        step: 100
      }
    }
    return {
      min: 10,
      max: 30,
      step: 1
    }
  }

  renderUserSettingsModal = () => {
    const data = this.enableUserSettings()
    if (!data.enabled) return null

    return (
      <Modal
        visible={this.state.showUserSettingsModal}
        onClose={() => this.setState({ showUserSettingsModal: false })}
        size="3xl"
        showCloseButton
      >
        {this.availableAntennaSettings() && (
          <Box>
            <ModalTitle title={__(T.userSettings.antennas)} />
            <Box mt={20}>
              {data.antennas.map((antenna, index) => {
                if (this.isMediop200u(antenna)) return null
                const mediol40 = this.isMediol40(antenna)
                const range = this.getPowerRange(antenna)
                return (
                  <Box flex key={antenna.id} mb={index < data.antennas.length - 1 ? 20 : 0} ph={0}>
                    <Text bold>
                      {antenna.description} - {antenna.reader.deviceType}
                    </Text>
                    <SliderSettings
                      title={__(T.misc.power)}
                      workstationId={AppStore.defaultWorkstation?.id ?? ''}
                      antenna={antenna}
                      field="txPower"
                      min={range.min}
                      max={range.max}
                      step={range.step}
                    />
                    {!mediol40 && (
                      <SliderSettings
                        title={__(T.misc.sensitivity)}
                        workstationId={AppStore.defaultWorkstation?.id ?? ''}
                        antenna={antenna}
                        field="rxSensitivity"
                        min={-80}
                        max={0}
                      />
                    )}
                  </Box>
                )
              })}
            </Box>
          </Box>
        )}
        {data.userSettings.length > 0 && (
          <Box mt={data.antennas.length > 0 ? 20 : 0}>
            <ModalTitle title={__(T.userSettings.title)} />
            <Box mt={20}>
              {data.userSettings.map((setting) => {
                const label =
                  setting.label ??
                  (T.userSettings[setting.name] === undefined
                    ? setting.name
                    : (__(T.userSettings[setting.name]) ?? setting.name))
                return (
                  <Box flex key={setting.name} pv={10}>
                    <Box row vcenter>
                      {setting.type === 'boolean' && (
                        <ToggleField
                          testId={`user-settings-toggle-${setting.name}`}
                          label={label}
                          checked={this.state.userSettings[setting.name] as boolean}
                          onChange={() => {
                            this.handleUserSettingsChange(
                              setting.name,
                              !(this.state.userSettings[setting.name] as boolean)
                            )
                          }}
                        />
                      )}
                      {setting.type === 'string' && (
                        <Box row flex vcenter>
                          <UserSettingsText style={{ marginRight: 30 }}>{label}</UserSettingsText>
                          <UserTextInput
                            type="text"
                            value={this.state.userSettings[setting.name] as string}
                            onChange={(e) => {
                              this.handleUserSettingsChange(setting.name, e.target.value)
                            }}
                          />
                        </Box>
                      )}
                      {setting.type === 'number' && (
                        <Box row flex vcenter>
                          <UserSettingsText style={{ marginRight: 20 }}>{label}</UserSettingsText>
                          <UserTextInput
                            type="number"
                            value={this.state.userSettings[setting.name]?.toString() ?? 0}
                            onChange={(e) => {
                              this.handleUserSettingsChange(
                                setting.name,
                                Number.isNaN(e.target.value) ? 0 : Number(e.target.value)
                              )
                            }}
                          />
                        </Box>
                      )}
                      {setting.type === 'select' && (
                        <Box row flex vcenter>
                          <UserSettingsText style={{ marginRight: 20 }}>{label}</UserSettingsText>
                          <UserSelect
                            onChange={(e) => {
                              this.handleUserSettingsChange(setting.name, e.target.value)
                            }}
                          >
                            {(setting as SelectOperationUserSetting).options?.map((option) => {
                              const optionLabel =
                                option.label ??
                                (T.userSettings[option.key] === undefined
                                  ? option.key
                                  : (__(T.userSettings[option.key]) ?? option.key))
                              return (
                                <option
                                  key={option.key}
                                  value={option.value.toString()}
                                  selected={this.state.userSettings[setting.name] === option.value.toString()}
                                >
                                  {optionLabel}
                                </option>
                              )
                            })}
                          </UserSelect>
                        </Box>
                      )}
                      {setting.type === 'autocomplete' && (
                        <Box row flex vcenter>
                          <UserSettingsText style={{ marginRight: 20 }}>{label}</UserSettingsText>
                          {this.state.userSettingsAutocompleteOptions[setting.name]?.length > 0 && (
                            <UserAutocomplete
                              value={(this.state.userSettings[setting.name] as string) || ''}
                              onChange={(e) => {
                                if (e.target.value === 'none') return
                                this.handleUserSettingsChange(setting.name, e.target.value)
                              }}
                            >
                              <option value="none">{__(T.misc.select_an_option)}</option>
                              {this.state.userSettingsAutocompleteOptions[setting.name]?.map((option) => {
                                return (
                                  <option key={option.key} value={option.value.toString()}>
                                    {option.label ?? option.key}
                                  </option>
                                )
                              })}
                            </UserAutocomplete>
                          )}
                          {this.state.userSettingsAutocompleteOptions[setting.name]?.length === 0 &&
                            (setting as AutocompleteOperationUserSetting).fallbackToStringType && (
                              <UserTextInput
                                type="text"
                                value={this.state.userSettings[setting.name] as string}
                                onChange={(e) => {
                                  this.handleUserSettingsChange(setting.name, e.target.value)
                                }}
                              />
                            )}
                          {this.state.userSettingsAutocompleteOptions[setting.name]?.length === 0 &&
                            !(setting as AutocompleteOperationUserSetting).fallbackToStringType && (
                              <p style={{ color: 'red' }}>{__(T.error.not_found)}</p>
                            )}
                          {this.state.userSettings[setting.name] !== undefined &&
                            this.state.userSettingsAutocompleteOptions[setting.name]?.length > 0 &&
                            !this.state.userSettingsAutocompleteOptions[setting.name].some(
                              (option) => option.value.toString() === this.state.userSettings[setting.name].toString()
                            ) && (
                              <p style={{ fontStyle: 'italic', marginLeft: 20 }}>
                                {__(T.misc.current_value)}:{' '}
                                {this.state.userSettings[setting.name]
                                  ? `"${this.state.userSettings[setting.name]}"`
                                  : __(T.misc.not_set)}
                              </p>
                            )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Box>
        )}
        {data.userManualFilename && (
          <Box mt={20}>
            <Spacer />
            <Button
              flex
              style={{ fontSize: 20 }}
              variant="plain"
              onClick={() => UserManuals.openManualPdf(data.operationId)}
            >
              <Box row vcenter justify="space-between">
                <Text>{__(T.misc.operation_user_manual)}</Text>
                <Icons.ArrowRight />
              </Box>
            </Button>
          </Box>
        )}
      </Modal>
    )
  }

  render() {
    const {
      title,
      onBackPress,
      alertOnBackPress,
      header,
      headerRight,
      children,
      enableEmulation,
      contentWrapperColumn,
      forceDetails = false
    } = this.props
    const { showEmulationModal } = this.state
    let detailsPosition
    if (header?.details && header.details.formSchema.length > 0) {
      if (forceDetails) {
        detailsPosition = forceDetails
      } else {
        detailsPosition = this.getFormSchemaLength() <= 2 ? 'top' : 'right'
      }
    }

    return (
      <Hotkeys
        keyName="command+e,ctrl+e,ctrl+m,command+m,alt+m"
        onKeyDown={this.onKeyDown}
        disabled={!enableEmulation || !AppStore.getEmulation()}
      >
        <PageContainer>
          {header !== null && (
            <Header
              title={title}
              actions={header?.actions}
              details={detailsPosition === 'top' ? header?.details : undefined}
              rowMode
              onBackPress={() => {
                if (alertOnBackPress) {
                  if (confirm(__(T.misc.navBackWarning))) {
                    onBackPress ? onBackPress() : Router.navigate('/')
                  }
                } else {
                  onBackPress ? onBackPress() : Router.navigate('/')
                }
              }}
            >
              <Box flex row>
                {headerRight as ReactNode}
                {this.renderUserSettingsButton()}
              </Box>
            </Header>
          )}
          <EmulationButton enabled={!!enableEmulation} onEmulationClick={this.onKeyDown} />
          <ContentWrapper flex row={!contentWrapperColumn}>
            {children}
            {header?.details && detailsPosition === 'right' && (
              <LeftHeaderContainer>
                <PageFormDetail
                  changeButtonStyle={{ margin: 10, marginBottom: 15, marginLeft: 0 }}
                  detailStyle={{ overflowWrap: 'break-word' }}
                  initData={header.details.data}
                  formSchema={header.details.formSchema}
                  setFormData={header.details.setFormData}
                  resetFormData={header.details.resetFormData}
                  operationId={header.details.operationId}
                  validateForm={header.details.validateForm}
                />
              </LeftHeaderContainer>
            )}
          </ContentWrapper>
        </PageContainer>

        {enableEmulation && showEmulationModal && (
          <EmulationModal
            title="Emulation Modal"
            onClose={() => {
              this.setState({ showEmulationModal: false })
            }}
            onConfirm={this.onEmulationConfirm}
            isOpen
          />
        )}
        {this.renderUserSettingsModal()}
      </Hotkeys>
    )
  }
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  max-height: 100%;
`

const ContentWrapper = styled(Box)`
  overflow: hidden;
  background-color: ${({ theme }) => theme.background1};
  gap: 4px;
  padding-right: 4px;
`

const ContentInner = styled(Box)<{
  notBoxed?: boolean
  overflowY?: 'visible' | 'hidden' | 'clip' | 'scroll' | 'auto'
}>`
  overflow-y: ${({ overflowY = 'auto' }) => overflowY};
  scrollbar-width: none;
  margin: ${({ theme }) => theme.padding};
  box-shadow: ${({ notBoxed }) => (notBoxed ? 'none' : `0 2px 10px rgba(0, 0, 0, 0.05)`)};
  border-radius: ${({ notBoxed }) => (notBoxed ? 'none' : `15px`)};
  background-color: ${({ notBoxed, theme }) => (notBoxed ? 'inherit' : theme.background2)};
`

const LeftHeaderContainer = styled(Box)`
  background: #eaeaea;
  border-radius: 15px;
  margin: 15px;
  margin-left: 0px;
  padding-left: 15px;
  max-width: 20vw;
  min-width: 20vw;
  height: fit-content;
  overflow-y: auto;
  max-height: calc(100vh - 130px);
`

const SettingsButton = styled.div`
  background: #ffffff;
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: center;
  cursor: pointer;
  height: 77px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.2);
  }
`

const UserSettingsText = styled.label`
  font-weight: bold;
  font-size: 14px;
  text-align: center;
  text-align: right;
`
const UserTextInput = styled.input`
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  box-shadow: '0 0 0 3px rgba(47, 128, 237, 0.5)';
`
const UserSelect = styled.select`
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  box-shadow: '0 0 0 3px rgba(47, 128, 237, 0.5)';
`
const UserAutocomplete = styled.select`
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  box-shadow: '0 0 0 3px rgba(47, 128, 237, 0.5)';
`
